/**
 * The runtime store of one mounted dashboard: the draft document, viewer
 * choices (filters, cross-filters, per-tile controls), the engine session
 * and each data tile's run state. A zustand vanilla store provided through
 * React context, so every mounted dashboard owns its own. Not persisted.
 */

import { createContext, createElement, ReactNode, useContext } from "react"
import { createStore, StoreApi, useStore } from "zustand"
import type {
  EngineOutputItem,
  HierarchicalDataEntry,
} from "types/engineOutput.types"
import type { TableModel } from "utils/tableModel"
import {
  DashboardData,
  DashboardDocument,
  FilterDef,
  Page,
  Tile,
  TileLayout,
  newId,
  isDataTile,
} from "features/Dashboard/types/dashboardDocument"
import { CrossFilterEntry, liveFilterSelections } from "./universeComposer"
import { retargetScript, TileControlState } from "./tileScript"
import type { PrepPart } from "./prepScript"

export type DashboardMode = "build" | "view"
export type TileStatus =
  | "idle"
  | "queued"
  | "running"
  | "ready"
  | "stale"
  | "error"
/** One panel of a repeated tile: the script it ran and what came back. */
export interface PanelRuntime {
  script: string
  model?: TableModel
  error?: string
}
export interface TileRuntime {
  status: TileStatus
  /** What last ran: a repeated tile's panel scripts, one after another. */
  script?: string
  result?: EngineOutputItem
  model?: TableModel
  /**
   * Text the tile shows instead of drawing its model: the prompt of a tile
   * with nothing to run yet, or an output that is not a table.
   */
  message?: string
  error?: string
  usesUniverse: boolean
  /**
   * A repeated tile's panels by category, in place of `model`. The tile is
   * ready once every panel has a model or an error, and in error only when
   * every panel failed.
   */
  panels?: Record<string, PanelRuntime>
}
export interface SessionState {
  instance: string | null
  /**
   * "prep-error": the data is open and the codebook loaded, but the
   * preparation failed (`prepError` says where and why). The dashboard stays
   * open so the Data panel can fix it; no tile runs until it is fixed.
   */
  status: "idle" | "starting" | "ready" | "prep-error" | "error"
  error?: string
  prepError?: string
  knownFilterFields: Set<string>
  /** Bumped on every RESET so runners drop results of the previous session. */
  generation: number
  /** The parts of the preparation the latest RESET sequence ran, for naming them in a trace. */
  preparation?: PrepPart[]
}

export interface DashboardState {
  projectId: string
  name: string
  document: DashboardDocument
  savedSnapshot: string
  mode: DashboardMode
  activePageId: string
  selectedTileId: string | null
  /** Whether the Data panel (preparation, weighting, colours) is open. */
  dataPanelOpen: boolean
  codebook: HierarchicalDataEntry[] | null
  session: SessionState
  filterSelections: Record<string, string[]>
  crossFilters: Record<string, CrossFilterEntry>
  tileControls: Record<string, TileControlState>
  tiles: Record<string, TileRuntime>
  /**
   * The tiles the assistant added or changed since the dashboard was last
   * saved (each shows an "AI" tag). `markSaved` clears them.
   */
  aiTileIds: string[]

  setDocument: (doc: DashboardDocument) => void
  updateDocument: (fn: (d: DashboardDocument) => DashboardDocument) => void
  setTitle: (title: string) => void
  setPrepScript: (script: string) => void
  /**
   * Sets the pipelines and the weight. Like a prep script edit, a change
   * restarts the session (the container watches the preparation), which
   * stales every tile.
   */
  setData: (data: DashboardData) => void
  /** Sets the theme (table style, colours) without running any tile. */
  setTheme: (theme: DashboardDocument["theme"]) => void
  setFilters: (filters: FilterDef[]) => void
  addPage: (title?: string) => string
  renamePage: (id: string, title: string) => void
  removePage: (id: string) => void
  movePage: (id: string, toIndex: number) => void
  setActivePage: (id: string) => void
  addTile: (pageId: string, tile: Tile) => void
  /** Edits a tile and marks it stale, so it runs again. */
  updateTile: (pageId: string, tileId: string, fn: (t: Tile) => Tile) => void
  /**
   * Edits a tile without marking it stale: for what the tile shows from its
   * last result (measures, which rows it shows), never for what it runs.
   * The document still becomes dirty.
   */
  updateTileView: (
    pageId: string,
    tileId: string,
    fn: (t: Tile) => Tile
  ) => void
  removeTile: (pageId: string, tileId: string) => void
  duplicateTile: (pageId: string, tileId: string) => void
  setTileLayouts: (pageId: string, layouts: Record<string, TileLayout>) => void

  setMode: (mode: DashboardMode) => void
  selectTile: (id: string | null) => void
  setDataPanelOpen: (open: boolean) => void
  setFilterSelection: (filterId: string, values: string[]) => void
  resetFilterSelections: () => void
  toggleCrossFilter: (
    tileId: string,
    variable: string,
    category: string
  ) => void
  clearCrossFilter: (tileId: string) => void
  clearAllViewerState: () => void
  setTileControl: (tileId: string, patch: Partial<TileControlState>) => void

  setSession: (patch: Partial<SessionState>) => void
  setCodebook: (codebook: HierarchicalDataEntry[] | null) => void
  markKnownFilterField: (name: string) => void
  resetKnownFilterFields: () => void

  markStale: (tileIds: string[]) => void
  markAllStale: () => void
  setTileQueued: (tileId: string, script: string, usesUniverse: boolean) => void
  setTileRunning: (tileId: string) => void
  applyTileResult: (
    tileId: string,
    script: string,
    result: EngineOutputItem,
    model: TableModel,
    message?: string
  ) => void
  setTileError: (tileId: string, script: string, message: string) => void
  /** Queues a repeated tile's panels, each with no result yet. */
  setPanelsQueued: (
    tileId: string,
    panels: { category: string; script: string }[],
    usesUniverse: boolean
  ) => void
  applyPanelResult: (
    tileId: string,
    category: string,
    script: string,
    model: TableModel
  ) => void
  setPanelError: (
    tileId: string,
    category: string,
    script: string,
    message: string
  ) => void
  retryTile: (tileId: string) => void

  /**
   * Records `saved` (the draft when omitted) as what is stored, and lets go
   * of the "AI" tags: all of them, or, when the draft has moved on since
   * `saved` was sent, those of tiles that are still as they were saved.
   */
  markSaved: (saved?: DashboardDocument) => void
  /**
   * Puts the assistant's proposed document in place of the draft, as an
   * edit (unlike `setDocument`, what is saved stays, so the dashboard is
   * dirty until it is saved). `aiTileIds` (the tiles it added or changed)
   * join the tagged ones the document still has, and the page of the first
   * of them is shown. Every data tile runs again.
   */
  loadDraft: (document: DashboardDocument, aiTileIds: string[]) => void
  /**
   * Puts an earlier draft back (the Undo of a proposal), as an edit. The
   * tagged tiles become `aiTileIds` when given, else those of the tagged
   * ones the document still has. Every data tile runs again.
   */
  revertTo: (document: DashboardDocument, aiTileIds?: string[]) => void
}

const defaultSelections = (doc: DashboardDocument): Record<string, string[]> =>
  Object.fromEntries(doc.filters.map((f) => [f.id, [...f.defaultSelection]]))

const dataTileIds = (doc: DashboardDocument): string[] =>
  doc.pages.flatMap((p) => p.tiles.filter(isDataTile).map((t) => t.id))

const tileIdsOf = (doc: DashboardDocument): Set<string> =>
  new Set(doc.pages.flatMap((p) => p.tiles.map((t) => t.id)))

/** Each tile of `doc` as JSON, by id. */
const tileJson = (doc: DashboardDocument): Map<string, string> =>
  new Map(
    doc.pages.flatMap((p) => p.tiles.map((t) => [t.id, JSON.stringify(t)]))
  )

/**
 * The filter selections once the filters become `filters`: a surviving
 * filter keeps its selection unless its variable changed; a new one starts
 * from its default.
 */
const carriedSelections = (
  before: FilterDef[],
  selections: Record<string, string[]>,
  filters: FilterDef[]
): Record<string, string[]> => {
  const previous = new Map(before.map((f) => [f.id, f.variable]))
  return Object.fromEntries(
    filters.map((f) => {
      const kept =
        previous.get(f.id) === f.variable ? selections[f.id] : undefined
      return [f.id, kept ?? [...f.defaultSelection]]
    })
  )
}

const editPage = (
  doc: DashboardDocument,
  pageId: string,
  fn: (p: Page) => Page
): DashboardDocument => ({
  ...doc,
  pages: doc.pages.map((p) => (p.id === pageId ? fn(p) : p)),
})

/** The filter conditions that reach the universe, order-independent. */
const filterConditions = (
  filters: FilterDef[],
  selections: Record<string, string[]>
): string =>
  JSON.stringify(
    filters
      .map((f) => [f.variable, ...[...(selections[f.id] ?? [])].sort()])
      .filter((c) => c.length > 1)
      .sort((a, b) => a.join("|").localeCompare(b.join("|")))
  )

/**
 * A repeated tile's status once a panel lands: still `current` while any
 * panel waits, else ready, or an error (the first panel's message) when
 * every panel failed.
 */
const panelsStatus = (
  panels: Record<string, PanelRuntime>,
  current: TileStatus
): { status: TileStatus; error?: string } => {
  const all = Object.values(panels)
  if (all.some((p) => p.model === undefined && p.error === undefined)) {
    return { status: current }
  }
  const failed = all.filter((p) => p.error !== undefined)
  return failed.length === all.length
    ? { status: "error", error: failed[0]?.error }
    : { status: "ready", error: undefined }
}

/** `record` without the keys in `ids`, and whether any key was dropped. */
const omitIds = <T>(
  record: Record<string, T>,
  ids: Set<string>
): [Record<string, T>, boolean] => {
  const kept = Object.entries(record).filter(([id]) => !ids.has(id))
  return [Object.fromEntries(kept), kept.length !== Object.keys(record).length]
}

export const createDashboardStore = (init: {
  document: DashboardDocument
  mode: DashboardMode
  projectId: string
  name: string
}): StoreApi<DashboardState> =>
  createStore<DashboardState>((set, get) => {
    /**
     * Drops the runtime, cross-filter and controls of removed tiles. A dropped
     * cross-filter changes what every remaining data tile shows, so those go
     * stale.
     */
    const forgetTiles = (ids: string[]): void => {
      const gone = new Set(ids)
      const s = get()
      const [tiles] = omitIds(s.tiles, gone)
      const [crossFilters, droppedCrossFilter] = omitIds(s.crossFilters, gone)
      const [tileControls] = omitIds(s.tileControls, gone)
      set({ tiles, crossFilters, tileControls })
      if (droppedCrossFilter) get().markAllStale()
    }

    /**
     * Replaces the whole draft (an assistant proposal or its Undo) as an
     * edit: the saved snapshot stays. The tags are `tagged` that the
     * document has; the page shown is that of `show` when given, else the
     * one shown now if the document still has it, else the first. Every
     * runtime goes and every data tile is stale, so the new tiles run;
     * cross-filters and controls of tiles that are gone go with them.
     */
    const replaceDocument = (
      document: DashboardDocument,
      tagged: string[],
      show?: string
    ): void => {
      const s = get()
      const ids = tileIdsOf(document)
      const kept = <T>(record: Record<string, T>): Record<string, T> =>
        Object.fromEntries(Object.entries(record).filter(([id]) => ids.has(id)))
      const pageOf = (tileId: string): string | undefined =>
        document.pages.find((p) => p.tiles.some((t) => t.id === tileId))?.id
      const current = document.pages.some((p) => p.id === s.activePageId)
        ? s.activePageId
        : undefined
      set({
        document,
        aiTileIds: [...new Set(tagged)].filter((id) => ids.has(id)),
        activePageId:
          (show !== undefined ? pageOf(show) : undefined) ??
          current ??
          document.pages[0]?.id ??
          "",
        selectedTileId:
          s.selectedTileId !== null && ids.has(s.selectedTileId)
            ? s.selectedTileId
            : null,
        filterSelections: carriedSelections(
          s.document.filters,
          s.filterSelections,
          document.filters
        ),
        crossFilters: kept(s.crossFilters),
        tileControls: kept(s.tileControls),
        tiles: {},
      })
      get().markAllStale()
    }

    return {
      projectId: init.projectId,
      name: init.name,
      document: init.document,
      savedSnapshot: JSON.stringify(init.document),
      mode: init.mode,
      activePageId: init.document.pages[0]?.id ?? "",
      selectedTileId: null,
      dataPanelOpen: false,
      codebook: null,
      session: {
        instance: null,
        status: "idle",
        knownFilterFields: new Set(),
        generation: 0,
      },
      filterSelections: defaultSelections(init.document),
      crossFilters: {},
      tileControls: {},
      tiles: {},
      aiTileIds: [],

      setDocument: (document) =>
        set({
          document,
          savedSnapshot: JSON.stringify(document),
          activePageId: document.pages[0]?.id ?? "",
          filterSelections: defaultSelections(document),
          crossFilters: {},
          tileControls: {},
          tiles: {},
        }),
      updateDocument: (fn) => set((s) => ({ document: fn(s.document) })),
      setTitle: (title) => get().updateDocument((d) => ({ ...d, title })),
      setPrepScript: (prepScript) =>
        get().updateDocument((d) => ({ ...d, prepScript })),
      setData: (data) => get().updateDocument((d) => ({ ...d, data })),
      // The theme is how results are drawn, never what runs: no tile goes
      // stale.
      setTheme: (theme) => get().updateDocument((d) => ({ ...d, theme })),
      setFilters: (filters) => {
        const before = get()
        const document = { ...before.document, filters }
        const filterSelections = carriedSelections(
          before.document.filters,
          before.filterSelections,
          filters
        )
        set({ document, filterSelections })
        // Only a change in what the filters select re-runs tiles (not a label).
        const changed =
          filterConditions(filters, filterSelections) !==
          filterConditions(before.document.filters, before.filterSelections)
        if (changed) get().markAllStale()
      },
      addPage: (title) => {
        const id = newId("page")
        set((s) => ({
          document: {
            ...s.document,
            pages: [
              ...s.document.pages,
              {
                id,
                title: title ?? `Page ${s.document.pages.length + 1}`,
                tiles: [],
              },
            ],
          },
          activePageId: id,
        }))
        return id
      },
      renamePage: (id, title) =>
        get().updateDocument((d) => editPage(d, id, (p) => ({ ...p, title }))),
      removePage: (id) => {
        const s = get()
        const page = s.document.pages.find((p) => p.id === id)
        if (!page || s.document.pages.length <= 1) return
        const pages = s.document.pages.filter((p) => p.id !== id)
        set({
          document: { ...s.document, pages },
          activePageId: s.activePageId === id ? pages[0].id : s.activePageId,
          selectedTileId: null,
        })
        forgetTiles(page.tiles.map((t) => t.id))
      },
      movePage: (id, toIndex) =>
        get().updateDocument((d) => {
          const pages = [...d.pages]
          const from = pages.findIndex((p) => p.id === id)
          if (from < 0) return d
          const [page] = pages.splice(from, 1)
          pages.splice(Math.max(0, Math.min(toIndex, pages.length)), 0, page)
          return { ...d, pages }
        }),
      setActivePage: (activePageId) =>
        set({ activePageId, selectedTileId: null }),
      addTile: (pageId, tile) => {
        get().updateDocument((d) =>
          editPage(d, pageId, (p) => ({ ...p, tiles: [...p.tiles, tile] }))
        )
        if (isDataTile(tile)) get().markStale([tile.id])
        set({ selectedTileId: tile.id })
      },
      updateTile: (pageId, tileId, fn) => {
        get().updateTileView(pageId, tileId, fn)
        get().markStale([tileId])
      },
      updateTileView: (pageId, tileId, fn) =>
        get().updateDocument((d) =>
          editPage(d, pageId, (p) => ({
            ...p,
            tiles: p.tiles.map((t) => (t.id === tileId ? fn(t) : t)),
          }))
        ),
      removeTile: (pageId, tileId) => {
        set((s) => ({
          document: editPage(s.document, pageId, (p) => ({
            ...p,
            tiles: p.tiles.filter((t) => t.id !== tileId),
          })),
          selectedTileId: s.selectedTileId === tileId ? null : s.selectedTileId,
        }))
        forgetTiles([tileId])
      },
      duplicateTile: (pageId, tileId) => {
        const page = get().document.pages.find((p) => p.id === pageId)
        const tile = page?.tiles.find((t) => t.id === tileId)
        if (!tile) return
        const id = newId("tile")
        const copy = {
          ...structuredClone(tile),
          id,
          layout: { ...tile.layout, y: tile.layout.y + tile.layout.h },
        } as Tile
        if (isDataTile(copy) && copy.source.kind === "script") {
          copy.source = {
            ...copy.source,
            script: retargetScript(copy.source.script, tile.id, id),
          }
        }
        get().addTile(pageId, copy)
      },
      setTileLayouts: (pageId, layouts) =>
        get().updateDocument((d) =>
          editPage(d, pageId, (p) => ({
            ...p,
            tiles: p.tiles.map((t) =>
              layouts[t.id] ? { ...t, layout: layouts[t.id] } : t
            ),
          }))
        ),

      // The Data panel belongs to Build mode, like the inspector.
      setMode: (mode) =>
        set({ mode, selectedTileId: null, dataPanelOpen: false }),
      selectTile: (selectedTileId) => set({ selectedTileId }),
      setDataPanelOpen: (dataPanelOpen) => set({ dataPanelOpen }),
      setFilterSelection: (filterId, values) => {
        set((s) => ({
          filterSelections: { ...s.filterSelections, [filterId]: values },
        }))
        get().markAllStale()
      },
      resetFilterSelections: () => {
        set((s) => ({ filterSelections: defaultSelections(s.document) }))
        get().markAllStale()
      },
      toggleCrossFilter: (tileId, variable, category) => {
        set((s) => {
          const current = s.crossFilters[tileId]
          const categories =
            current && current.variable === variable
              ? [...current.categories]
              : []
          const i = categories.indexOf(category)
          if (i >= 0) categories.splice(i, 1)
          else categories.push(category)
          const [rest] = omitIds(s.crossFilters, new Set([tileId]))
          return {
            crossFilters:
              categories.length > 0
                ? { ...rest, [tileId]: { variable, categories } }
                : rest,
          }
        })
        get().markStale(
          dataTileIds(get().document).filter((id) => id !== tileId)
        )
      },
      clearCrossFilter: (tileId) => {
        set((s) => ({
          crossFilters: omitIds(s.crossFilters, new Set([tileId]))[0],
        }))
        get().markStale(
          dataTileIds(get().document).filter((id) => id !== tileId)
        )
      },
      clearAllViewerState: () => {
        set((s) => ({
          filterSelections: defaultSelections(s.document),
          crossFilters: {},
          tileControls: {},
        }))
        get().markAllStale()
      },
      setTileControl: (tileId, patch) => {
        set((s) => ({
          tileControls: {
            ...s.tileControls,
            [tileId]: { ...s.tileControls[tileId], ...patch },
          },
        }))
        get().markStale([tileId])
      },

      setSession: (patch) =>
        set((s) => ({ session: { ...s.session, ...patch } })),
      setCodebook: (codebook) => set({ codebook }),
      markKnownFilterField: (name) =>
        set((s) => ({
          session: {
            ...s.session,
            knownFilterFields: new Set([...s.session.knownFilterFields, name]),
          },
        })),
      resetKnownFilterFields: () =>
        set((s) => ({
          session: {
            ...s.session,
            knownFilterFields: new Set(),
            generation: s.session.generation + 1,
          },
        })),

      markStale: (tileIds) =>
        set((s) => ({
          tiles: {
            ...s.tiles,
            ...Object.fromEntries(
              tileIds.map((id) => [
                id,
                {
                  ...(s.tiles[id] ?? { usesUniverse: true }),
                  status: "stale" as const,
                },
              ])
            ),
          },
        })),
      markAllStale: () => get().markStale(dataTileIds(get().document)),
      setTileQueued: (tileId, script, usesUniverse) =>
        set((s) => ({
          tiles: {
            ...s.tiles,
            [tileId]: {
              ...s.tiles[tileId],
              status: "queued",
              script,
              usesUniverse,
              error: undefined,
              panels: undefined,
            },
          },
        })),
      // The tile's single result goes: a repeated tile draws its panels.
      setPanelsQueued: (tileId, panels, usesUniverse) =>
        set((s) => ({
          tiles: {
            ...s.tiles,
            [tileId]: {
              ...s.tiles[tileId],
              status: "queued",
              script: panels.map((p) => p.script).join("\n\n"),
              usesUniverse,
              result: undefined,
              model: undefined,
              message: undefined,
              error: undefined,
              panels: Object.fromEntries(
                panels.map((p) => [p.category, { script: p.script }])
              ),
            },
          },
        })),
      // Only a queued tile starts running: a late call must not revive a stale
      // tile.
      setTileRunning: (tileId) =>
        set((s) => {
          const t = s.tiles[tileId]
          if (!t || t.status !== "queued") return {}
          return {
            tiles: { ...s.tiles, [tileId]: { ...t, status: "running" } },
          }
        }),
      // A result or error lands only on the run that asked for it: not after
      // the tile moved on to another script, and not once it went stale (a
      // stale tile is re-planned when its page is shown, even if the script
      // comes out the same).
      applyTileResult: (tileId, script, result, model, message) =>
        set((s) => {
          const t = s.tiles[tileId]
          if (!t || t.script !== script || t.status === "stale") return {}
          return {
            tiles: {
              ...s.tiles,
              [tileId]: {
                ...t,
                status: "ready",
                result,
                model,
                message,
                error: undefined,
              },
            },
          }
        }),
      setTileError: (tileId, script, message) =>
        set((s) => {
          const t = s.tiles[tileId]
          if (!t || t.script !== script || t.status === "stale") return {}
          return {
            tiles: {
              ...s.tiles,
              [tileId]: { ...t, status: "error", error: message },
            },
          }
        }),
      // As for a single run: a panel's result or error lands only while the
      // panel still waits on that script and the tile has not gone stale.
      applyPanelResult: (tileId, category, script, model) =>
        set((s) => {
          const t = s.tiles[tileId]
          const panel = t?.panels?.[category]
          if (!t?.panels || !panel || panel.script !== script) return {}
          if (t.status === "stale") return {}
          const panels = {
            ...t.panels,
            [category]: { script, model },
          }
          return {
            tiles: {
              ...s.tiles,
              [tileId]: { ...t, panels, ...panelsStatus(panels, t.status) },
            },
          }
        }),
      setPanelError: (tileId, category, script, message) =>
        set((s) => {
          const t = s.tiles[tileId]
          const panel = t?.panels?.[category]
          if (!t?.panels || !panel || panel.script !== script) return {}
          if (t.status === "stale") return {}
          const panels = {
            ...t.panels,
            [category]: { script, error: message },
          }
          return {
            tiles: {
              ...s.tiles,
              [tileId]: { ...t, panels, ...panelsStatus(panels, t.status) },
            },
          }
        }),
      retryTile: (tileId) => get().markStale([tileId]),

      markSaved: (saved) =>
        set((s) => {
          const stored = saved ?? s.document
          if (stored === s.document) {
            return { savedSnapshot: JSON.stringify(stored), aiTileIds: [] }
          }
          // A proposal loaded while the save was on its way keeps its tags.
          const asSaved = tileJson(stored)
          const now = tileJson(s.document)
          return {
            savedSnapshot: JSON.stringify(stored),
            aiTileIds: s.aiTileIds.filter((id) => now.get(id) !== asSaved.get(id)),
          }
        }),
      loadDraft: (document, aiTileIds) => {
        const ids = tileIdsOf(document)
        const shown = aiTileIds.find((id) => ids.has(id))
        replaceDocument(document, [...get().aiTileIds, ...aiTileIds], shown)
      },
      revertTo: (document, aiTileIds) =>
        replaceDocument(document, aiTileIds ?? get().aiTileIds),
    }
  })

/**
 * The model the Inspector lists a tile's rows and columns from: its result,
 * or for a repeated tile the first panel that has one (every panel has the
 * same rows and columns).
 */
export const runtimeModel = (
  rt: TileRuntime | undefined
): TableModel | undefined =>
  rt?.model ??
  Object.values(rt?.panels ?? {}).find((p) => p.model !== undefined)?.model

export const selectIsDirty = (s: DashboardState): boolean =>
  JSON.stringify(s.document) !== s.savedSnapshot
export const selectActivePage = (s: DashboardState): Page | undefined =>
  s.document.pages.find((p) => p.id === s.activePageId)
export const findTile = (
  s: DashboardState,
  id: string
): { page: Page; tile: Tile } | undefined => {
  for (const page of s.document.pages) {
    const tile = page.tiles.find((t) => t.id === id)
    if (tile) return { page, tile }
  }
  return undefined
}
/** Whether a filter or cross-filter applies; ignored selections do not count. */
export const selectAnyFilterActive = (s: DashboardState): boolean =>
  Object.values(
    liveFilterSelections(s.document.filters, s.filterSelections, s.codebook)
  ).some((v) => v.length > 0) || Object.keys(s.crossFilters).length > 0

export const DashboardStoreContext =
  createContext<StoreApi<DashboardState> | null>(null)
export const DashboardStoreProvider = ({
  store,
  children,
}: {
  store: StoreApi<DashboardState>
  children: ReactNode
}) => createElement(DashboardStoreContext.Provider, { value: store }, children)
export const useDashboardStoreApi = (): StoreApi<DashboardState> => {
  const store = useContext(DashboardStoreContext)
  if (!store)
    throw new Error(
      "useDashboardStore must be used inside DashboardStoreProvider"
    )
  return store
}
export const useDashboardStore = <T>(selector: (s: DashboardState) => T): T =>
  useStore(useDashboardStoreApi(), selector)
